import { useState, useEffect, useCallback } from 'react';
import { Alert, Badge, Button, Card, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { Check2, Clipboard, PersonPlus, Trash } from 'react-bootstrap-icons';
import { UserManagementService, User } from '@/services/userManagementService';
import { useAuth } from '@/contexts/AuthContext';
import { PageHeader } from '@/components/ui/PageHeader';
import { formatNzDate, formatNzDateTime, timeAgo } from '@/utils/relativeTime';
import './UserManagement.css';

/** How many people the list reads in one go. */
const LIST_LIMIT = 50;

/**
 * An account's status, in words. Null for the everyday case: the account is active. The portal
 * adds every account as active, so this cannot tell whether a new person chose a password yet.
 */
function statusOf(user: User): { label: string; tone: 'warning' | 'danger' } | null {
  if (!user.enabled) return { label: 'Disabled', tone: 'danger' };
  if (!user.staff) return { label: 'Not in the staff group: every page refuses them', tone: 'danger' };
  switch (user.status) {
    case 'CONFIRMED':
      return null;
    case 'FORCE_CHANGE_PASSWORD':
      return { label: 'Has not set a password', tone: 'warning' };
    case 'RESET_REQUIRED':
      return { label: 'Must reset their password', tone: 'warning' };
    case 'UNCONFIRMED':
      return { label: 'Not confirmed', tone: 'warning' };
    default: {
      const words = user.status.replace(/_/g, ' ').toLowerCase();
      return { label: words.charAt(0).toUpperCase() + words.slice(1), tone: 'warning' };
    }
  }
}

export default function UserManagement() {
  const { session, user: me } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [createdEmail, setCreatedEmail] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);
  /** The list could not be read. Until it loads again there is no list to show. */
  const [usersError, setUsersError] = useState<string | null>(null);
  /** A delete failed. The list on screen is still correct. */
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deletingUser, setDeletingUser] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [copied, setCopied] = useState(false);
  const [userManagementService, setUserManagementService] = useState<UserManagementService | null>(null);

  // Define fetchUsers before using it in useEffect dependency array
  const fetchUsers = useCallback(
    async (service?: UserManagementService) => {
      if (!service && !userManagementService) return;

      const serviceToUse = service || userManagementService!;
      setLoadingUsers(true);
      setUsersError(null);

      try {
        const result = await serviceToUse.listUsers(LIST_LIMIT);
        setUsers(result.users);
        setHasMore(result.hasMore);
      } catch (err: unknown) {
        console.error('Error fetching users:', err);
        setUsersError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoadingUsers(false);
      }
    },
    [userManagementService]
  );

  useEffect(() => {
    const initializeService = async () => {
      if (!session?.idToken) {
        setUsersError('You are not signed in.');
        setLoadingUsers(false);
        return;
      }

      try {
        const config = await fetch('/config.json').then((res) => res.json());
        // Avoid recreating the service if already initialized
        const service =
          userManagementService ||
          new UserManagementService(config.AWS_REGION, config.IDENTITY_POOL_ID, config.USER_POOL_ID);
        if (!userManagementService) setUserManagementService(service);
        await fetchUsers(service);
      } catch (err) {
        console.error('Error initializing user management service:', err);
        setUsersError('The portal settings did not load.');
        setLoadingUsers(false);
      }
    };

    initializeService();
    // Once per sign-in: the ID token changes on every refresh, every few minutes.
  }, [session?.sid]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userManagementService) return;

    setLoading(true);
    setError(null);
    setSuccess(false);
    setCreatedEmail('');

    try {
      await userManagementService.createUser(email);
      setCreatedEmail(email);
      setSuccess(true);
      setEmail('');

      setTimeout(() => {
        fetchUsers();
      }, 1000);
    } catch (err: unknown) {
      setError(err instanceof Error && err.message ? err.message : 'The user was not added.');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setEmail('');
    setError(null);
    setSuccess(false);
    setCreatedEmail('');
    setCopied(false);
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    if (loading) return;
    setShowAddModal(false);
  };

  const handleDeleteUser = (user: User) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete || !userManagementService) return;

    setShowDeleteModal(false);
    setDeletingUser(userToDelete.username);
    setDeleteError(null);

    try {
      await userManagementService.deleteUser(userToDelete.username);
      await fetchUsers();
    } catch (err: unknown) {
      console.error('Error deleting user:', err);
      setDeleteError(`${userToDelete.email} was not deleted: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setDeletingUser(null);
      setUserToDelete(null);
    }
  };

  const cancelDeleteUser = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  // The steps name the buttons as the set-up page shows them, so the two always match.
  const getUserInstructions = (email: string) => {
    const baseUrl = window.location.origin;
    return `Welcome to the Numa Customer Success Portal.

Your account is ready, under this email address: ${email}

To set your password:
1. Go to ${baseUrl}/create-password
2. Enter your email address: ${email}
3. Select "Send activation code". A code arrives by email.
4. Enter the code and choose a password.
5. Go to ${baseUrl} and sign in with the new password.

If something does not work, ask the person who sent you this.`;
  };

  const handleCopyInstructions = async () => {
    if (!createdEmail) return;

    try {
      const instructions = getUserInstructions(createdEmail);
      await navigator.clipboard.writeText(instructions);
      setCopied(true);
    } catch (err) {
      console.error('Failed to copy instructions:', err);
    }
  };

  const isMe = (user: User) => Boolean(me?.email) && user.email.toLowerCase() === me!.email.toLowerCase();

  return (
    <div className="users-page">
      <PageHeader
        title="Portal users"
        actions={
          <Button variant="primary" onClick={openAddModal} disabled={!userManagementService}>
            <PersonPlus className="me-1" />
            Add a user
          </Button>
        }
      />

      {usersError && (
        <Alert variant="danger" className="users-alert">
          <span>The users did not load: {usersError}</span>
          {userManagementService && (
            <Button variant="outline-danger" size="sm" onClick={() => fetchUsers()}>
              Try again
            </Button>
          )}
        </Alert>
      )}
      {deleteError && (
        <Alert variant="danger" dismissible onClose={() => setDeleteError(null)}>
          {deleteError}
        </Alert>
      )}
      {success && createdEmail && !showAddModal && (
        <Alert variant="success" className="users-alert" dismissible onClose={() => setSuccess(false)}>
          <span>{createdEmail} was added. They need the set-up steps to choose a password.</span>
          <Button variant="outline-secondary" size="sm" onClick={() => setShowAddModal(true)}>
            Show the steps
          </Button>
        </Alert>
      )}

      <Card>
        <Table hover responsive className="mb-0 users-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Status</th>
              <th>Added</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {loadingUsers && (
              <tr>
                <td colSpan={4} className="users-empty">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading users
                </td>
              </tr>
            )}
            {!loadingUsers &&
              !usersError &&
              users.map((user) => {
                const status = statusOf(user);
                return (
                  <tr key={user.username}>
                    <td>
                      <span className="users-table__email">{user.email}</span>
                      {isMe(user) && <span className="users-table__you">You</span>}
                    </td>
                    <td>
                      {status ? (
                        <Badge bg={status.tone}>{status.label}</Badge>
                      ) : (
                        <span className="users-table__ok">Active</span>
                      )}
                    </td>
                    <td title={formatNzDateTime(user.created)}>
                      <div>{timeAgo(user.created)}</div>
                      <div className="users-table__sub">{formatNzDate(user.created)}</div>
                    </td>
                    <td className="users-table__action">
                      <Button
                        variant="outline-danger"
                        size="sm"
                        disabled={deletingUser === user.username}
                        onClick={() => handleDeleteUser(user)}
                      >
                        {deletingUser === user.username ? (
                          'Deleting'
                        ) : (
                          <>
                            <Trash size={12} className="me-1" />
                            Delete
                          </>
                        )}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            {!loadingUsers && !usersError && users.length === 0 && (
              <tr>
                <td colSpan={4} className="users-empty">
                  Nobody has access yet. Add the first user.
                </td>
              </tr>
            )}
            {!loadingUsers && usersError && (
              <tr>
                <td colSpan={4} className="users-empty">
                  The list is not shown until it loads again.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Card>
      {!loadingUsers && !usersError && users.length > 0 && (
        <p className="users-count">
          {hasMore
            ? `The first ${users.length} users. There are more than this list shows.`
            : `${users.length} ${users.length === 1 ? 'user' : 'users'}`}
        </p>
      )}

      {/* ── Add a user: the form, then what to send the new person ── */}
      <Modal show={showAddModal} onHide={closeAddModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{success ? 'User added' : 'Add a user'}</Modal.Title>
        </Modal.Header>
        {!success ? (
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                  {error}
                </Alert>
              )}
              <Form.Group>
                <Form.Label htmlFor="users-new-email">Email address</Form.Label>
                <Form.Control
                  id="users-new-email"
                  type="email"
                  placeholder="name@arcanum.ai"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  autoFocus
                />
                <Form.Text>
                  They sign in with this address, which must end in @arcanum.ai. The portal adds them to the staff
                  group. It sends them no email: after you add them, you get the steps to send on yourself.
                </Form.Text>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={closeAddModal} disabled={loading}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? 'Adding' : 'Add user'}
              </Button>
            </Modal.Footer>
          </Form>
        ) : (
          <>
            <Modal.Body>
              <p>
                <strong>{createdEmail}</strong> has an account but no password yet. Send them these steps so they can
                set one.
              </p>
              <pre className="users-steps">{getUserInstructions(createdEmail)}</pre>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="outline-secondary"
                onClick={() =>
                  window.open(
                    `${window.location.origin}/create-password?email=${encodeURIComponent(createdEmail)}`,
                    '_blank'
                  )
                }
              >
                Open the set-up page
              </Button>
              <Button variant="outline-secondary" onClick={handleCopyInstructions}>
                {copied ? <Check2 className="me-1" /> : <Clipboard className="me-1" />}
                {copied ? 'Copied' : 'Copy the steps'}
              </Button>
              <Button variant="primary" onClick={closeAddModal}>
                Done
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>

      {/* ── Delete a user: say what it does before it happens ── */}
      <Modal show={showDeleteModal} onHide={cancelDeleteUser} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete this user?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>{userToDelete?.email}</strong> can no longer sign in to this portal, from the moment you confirm.
          </p>
          <p className="users-delete__note">
            Their account is removed and cannot be brought back. To give them access again, add them as a new user and
            they set a new password. Nothing they did in the portal is undone.
          </p>
          {userToDelete && isMe(userToDelete) && (
            <Alert variant="warning" className="mb-0">
              This is your own account. You lose access as soon as you confirm.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={cancelDeleteUser}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDeleteUser}>
            Delete user
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
